import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area } from 'recharts';
import { getTemperatureGraph } from '../api';
import './TemperatureGraph.css';

const TemperatureGraph = ({ cameraId, minTemp, maxTemp }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getTemperatureGraph(cameraId, 24);
      const formatted = (result.data || []).map(point => ({
        ...point,
        time: new Date(point.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }));
      setData(formatted);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [cameraId]);

  useEffect(() => {
    // Первичная загрузка
    fetchData();

    // Запускаем интервал обновления раз в 20 минут (1200000 мс)
    timerRef.current = setInterval(() => {
      fetchData();
    }, 1200000); // 20 * 60 * 1000

    // Очищаем интервал при размонтировании или смене cameraId
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [cameraId, fetchData]);

  if (loading && data.length === 0) return <div className="graph-loading">Загрузка...</div>;
  if (error) return <div className="graph-error">Ошибка: {error}</div>;
  if (data.length === 0) return <div className="graph-no-data">Нет данных</div>;

  const lineColor = "var(--status-ok)";
  const areaFill = "var(--status-ok)";

  return (
    <div className="temperature-graph">
      {loading && <div className="graph-refreshing">Обновление...</div>}
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
          <XAxis 
            dataKey="time" 
            tick={{ fontSize: 9, fill: 'var(--text-secondary)' }}
            interval="preserveStartEnd"
            tickCount={8}
          />
          <YAxis 
            tick={{ fontSize: 9, fill: 'var(--text-secondary)' }}
            width={25}
          />
          <Tooltip 
            contentStyle={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-color)', fontSize: '11px' }}
          />
          <Area 
            type="monotone" 
            dataKey="temperature" 
            stroke="none" 
            fill={areaFill} 
            fillOpacity={0.2}
          />
          <Line 
            type="monotone" 
            dataKey="temperature" 
            stroke={lineColor} 
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 4, stroke: lineColor, strokeWidth: 1 }}
          />
          {minTemp !== undefined && minTemp !== null && (
            <ReferenceLine 
              y={minTemp} 
              stroke="var(--status-warning)" 
              strokeDasharray="3 3" 
              label={{ value: `${minTemp}°C`, fill: 'var(--text-secondary)', fontSize: 9, position: 'left' }} 
            />
          )}
          {maxTemp !== undefined && maxTemp !== null && (
            <ReferenceLine 
              y={maxTemp} 
              stroke="var(--status-warning)" 
              strokeDasharray="3 3" 
              label={{ value: `${maxTemp}°C`, fill: 'var(--text-secondary)', fontSize: 9, position: 'left' }} 
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TemperatureGraph;